/** Breve ausência no socket ainda conta como online (evita flicker). */
export const PRESENCE_DISAPPEAR_DEBOUNCE_MS = 3_000;

/** Tempo com card cinza “saindo” antes de remover da lista. */
export const PRESENCE_STALE_RETAIN_MS = 45_000;

/**
 * @param {object|null|undefined} detail
 * @returns {boolean}
 */
export function presenceRowHasXpro4(detail) {
  return (detail?.xpro4Tabs?.length ?? 0) > 0;
}

/**
 * Grupo de cliente para ordenação: 0 = Xpro 4, 1 = legado/indeterminado.
 * @param {object} row
 */
export function presenceRowClientGroup(row) {
  if (presenceRowHasXpro4(row.presence_detail)) return 0;
  const legacy = row.presence_detail?.legacyTabCount ?? 0;
  if (legacy > 0) return 1;
  return 1;
}

/**
 * Chave estável para ordenação dentro do mesmo grupo (menor = aparece antes).
 * @param {object} row
 */
export function presenceRowSortRank(row) {
  const stale = row.presenceStale === true ? 1 : 0;
  const client = presenceRowClientGroup(row);
  return stale * 100 + client;
}

/**
 * @param {object} a
 * @param {object} b
 */
export function comparePresenceRows(a, b) {
  const rank = presenceRowSortRank(a) - presenceRowSortRank(b);
  if (rank !== 0) return rank;

  const orderA = Number(a.presenceStableOrder) || 0;
  const orderB = Number(b.presenceStableOrder) || 0;
  if (orderA !== orderB) return orderA - orderB;

  const w = String(a.workspace_host || "").localeCompare(String(b.workspace_host || ""), undefined, {
    sensitivity: "base",
  });
  if (w !== 0) return w;

  return String(a.nome || "").localeCompare(String(b.nome || ""), undefined, { sensitivity: "base" });
}

/**
 * Mescla snapshot ao vivo com cache para debounce de saída e cards “saindo”.
 * @param {object[]} rawRows
 * @param {Map<string, object>} cacheRef — mutável; persiste entre renders
 * @param {{ now?: number, orderCounterRef?: { current: number } }} [opts]
 * @returns {object[]}
 */
export function stabilizePresenceRows(rawRows, cacheRef, opts = {}) {
  const now = opts.now ?? Date.now();
  const orderCounterRef = opts.orderCounterRef;
  const cache = cacheRef;
  const rawByKey = new Map();

  for (const row of rawRows) {
    if (row?.key) rawByKey.set(row.key, row);
  }

  for (const [key, fresh] of rawByKey) {
    const prev = cache.get(key);
    if (prev) {
      cache.set(key, {
        ...prev,
        row: fresh,
        lastLiveAt: now,
        staleSince: null,
      });
    } else {
      const orderIndex = orderCounterRef ? orderCounterRef.current++ : now;
      cache.set(key, {
        row: fresh,
        firstSeenAt: now,
        lastLiveAt: now,
        staleSince: null,
        orderIndex,
      });
    }
  }

  for (const [key, entry] of cache) {
    if (rawByKey.has(key)) continue;
    const goneFor = now - entry.lastLiveAt;
    if (goneFor >= PRESENCE_DISAPPEAR_DEBOUNCE_MS && entry.staleSince == null) {
      entry.staleSince = now;
    }
  }

  const out = [];
  for (const [key, entry] of cache) {
    const isLive = rawByKey.has(key);
    if (isLive) {
      out.push({
        ...entry.row,
        presenceStale: false,
        presenceStableOrder: entry.orderIndex,
        presenceLastLiveAt: entry.lastLiveAt,
      });
      continue;
    }

    if (entry.staleSince == null) {
      out.push({
        ...entry.row,
        presenceStale: false,
        presenceStableOrder: entry.orderIndex,
        presenceLastLiveAt: entry.lastLiveAt,
      });
      continue;
    }

    const staleFor = now - entry.staleSince;
    if (staleFor >= PRESENCE_STALE_RETAIN_MS) {
      cache.delete(key);
      continue;
    }

    out.push({
      ...entry.row,
      presenceStale: true,
      presenceStableOrder: entry.orderIndex,
      presenceLastLiveAt: entry.lastLiveAt,
      presenceStaleSince: entry.staleSince,
    });
  }

  out.sort(comparePresenceRows);
  return out;
}
